import { Badge, ServerTable, SkeletonSpinner, Text } from '@civilgrid/ui/components';
import { buildSearchPayload, useTableSettings, useTableStoreState } from '@civilgrid/ui/hooks';
import type { FilterShortcut } from '@civilgrid/ui/utils';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useId, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { getCivilSearchers, listFilteredDataRequests } from '~/api/civilsearch';
import type { DataRequestV2 } from '~/common/types';
import rootStore from '~/store';
import { FetchErrorDisplay } from '../common/FetchErrorDisplay';
import { EditNotesDialog, FollowUpDialog, ReassignDialog, useBulkActions } from './bulkActions';
import { useColumns } from './columns';
import {
  getAllOpenRequests,
  getAssignedToMe,
  getFlaggedRequests,
  getNeedsFollowUp,
  getOpenRequestsNeedingFollowup,
  getPendingRequests,
} from './filterShortcuts';
import { parseUrlFilters } from './parseUrlFilters';

const TABLE_ID = 'data-requests-v2';

const getRequestRowLabel = (row: DataRequestV2) => `request ${row.requestId}`;

export default function DataRequests() {
  const headingId = useId();
  const columns = useColumns();
  const { me } = useSnapshot(rootStore) as typeof rootStore;

  // Table settings and state
  const { initialPageSize } = useTableSettings({ id: TABLE_ID });

  // TODO: remove once tanstack router is implemented
  const [urlFilters] = useState(() => {
    const parsed = parseUrlFilters();
    if (parsed) sessionStorage.removeItem('table-filter-state-cs-data-requests');
    return parsed;
  });

  const stateControl = useTableStoreState({
    defaultPageSize: initialPageSize ?? 100,
    initialSorting: [{ id: 'createdAt', desc: true }],
    initialFilters: urlFilters ?? getAssignedToMe(me?.id),
    storageKey: 'cs-data-requests',
  });

  const { pagination, sorting, filters, globalFilter } = stateControl;

  const shortcuts: FilterShortcut[] = useMemo(
    () => [
      { label: 'All Open', getFilter: getAllOpenRequests },
      { label: 'In Queue', getFilter: getPendingRequests },
      {
        label: 'Assigned To Me',
        getFilter: () => getAssignedToMe(me?.id),
      },
      { label: 'Flagged', getFilter: getFlaggedRequests },
      {
        label: 'Needs Followup',
        getFilter: () => getNeedsFollowUp(me?.id),
      },
      {
        label: 'Open Needing Followup',
        getFilter: () => getOpenRequestsNeedingFollowup(me?.id),
      },
    ],
    [me?.id],
  );

  const query = useQuery({
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryKey: ['data-requests-v2', { pagination, sorting, filters, globalFilter }],
    queryFn: async () => {
      const searchPayload = buildSearchPayload({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting,
        filters,
        globalFilter,
      });
      const apiPayload = {
        ...searchPayload,
        sort: searchPayload.sort?.[0],
      };
      const res = await listFilteredDataRequests({
        search: apiPayload,
      });
      return res;
    },
  });

  const civilSearchersQuery = useQuery({
    queryKey: ['civil-searchers'],
    queryFn: getCivilSearchers,
    staleTime: 5 * 60 * 1000,
  });

  const buildSearchPayloadForBulk = useCallback(() => {
    const searchPayload = buildSearchPayload({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sorting,
      filters,
      globalFilter,
    });
    return {
      ...searchPayload,
      sort: searchPayload.sort?.[0],
    };
  }, [pagination, sorting, filters, globalFilter]);

  const getActiveQueryKeyParams = useCallback(
    () => ({ pagination, sorting, filters, globalFilter }),
    [pagination, sorting, filters, globalFilter],
  );

  const getDataRequestById = useCallback(
    (id: string): DataRequestV2 | undefined => query.data?.requests.find((r) => r.id === id),
    [query.data],
  );

  const bulk = useBulkActions({
    civilSearchers: civilSearchersQuery.data ?? [],
    buildSearchPayloadForBulk,
    getActiveQueryKeyParams,
    getDataRequestById,
  });

  if (query.isError) {
    return (
      <FetchErrorDisplay
        label="data requests"
        error={query.error}
        onRetry={() => query.refetch()}
        filterStorageKey="table-filter-state-cs-data-requests"
      />
    );
  }

  return (
    <div className="flex h-screen-height-minus-top-nav flex-col">
      <div className="flex items-center gap-3 mb-3 mt-2">
        <Text id={headingId} className="text-primary text-xl font-semibold">
          Data Requests
        </Text>
        <Badge kind="count">
          {query.isLoading ? <SkeletonSpinner size={18} /> : (query.data?.totalDocuments.toLocaleString() ?? 0)}
        </Badge>
      </div>
      <div className="min-h-0 flex-1 mb-2">
        <ServerTable
          id={TABLE_ID}
          aria-labelledby={headingId}
          columns={columns}
          data={{
            data: query.data?.requests ?? [],
            rowCount: query.data?.totalDocuments ?? 0,
            isLoading: query.isLoading,
            isFetching: query.isFetching,
            error: query.error || null,
          }}
          stateControl={stateControl}
          enableColumnGrouping={false}
          filterShortcuts={shortcuts}
          isLoading={query.isLoading}
          className="h-full"
          bulkActions={bulk.bulkActions}
          getRowLabel={getRequestRowLabel}
        />
      </div>
      <ReassignDialog
        open={bulk.reassign.open}
        onClose={bulk.reassign.close}
        assigneeId={bulk.reassign.assigneeId}
        onAssigneeIdChange={bulk.reassign.setAssigneeId}
        assigneeOptions={bulk.reassign.assigneeOptions}
        onSubmit={bulk.reassign.onSubmit}
        isPending={bulk.reassign.isPending}
      />
      <EditNotesDialog
        open={bulk.editNotes.open}
        onClose={bulk.editNotes.close}
        notes={bulk.editNotes.notes}
        onNotesChange={bulk.editNotes.setNotes}
        onSubmit={bulk.editNotes.onSubmit}
        isPending={bulk.editNotes.isPending}
        title="Edit Internal Notes"
        label="Append to Internal Notes"
      />
      <EditNotesDialog
        open={bulk.editAgencyNotes.open}
        onClose={bulk.editAgencyNotes.close}
        notes={bulk.editAgencyNotes.notes}
        onNotesChange={bulk.editAgencyNotes.setNotes}
        onSubmit={bulk.editAgencyNotes.onSubmit}
        isPending={bulk.editAgencyNotes.isPending}
        title="Edit Agency Notes"
        label="Append to Agency Notes"
      />
      <FollowUpDialog
        open={bulk.editFollowUp.open}
        onClose={bulk.editFollowUp.close}
        fields={bulk.editFollowUp.fields}
        setField={bulk.editFollowUp.setField}
        hasChanges={bulk.editFollowUp.hasChanges}
        onSubmit={bulk.editFollowUp.onSubmit}
        isPending={bulk.editFollowUp.isPending}
      />
    </div>
  );
}
